import { MenuItem, TextField } from "@mui/material";
import { escapeRegExp } from "lodash";
import {
  useMemo,
  useState,
  type ChangeEvent,
  type FC,
  type ReactNode,
} from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";

import {
  DirectionEnum,
  formatDate,
  MAIN_DATE_FORMAT,
  NO_VALUE_DISPLAY,
  onActivateKeyDown,
  resolveCourseScheduleExistingRideStatusText,
  resolveCourseScheduleExistingStatusLabel,
  RideStatusEnum,
  useDrawers,
  useFormats,
  useHaveCustomPermissions,
  useTimezone,
  type ICourseDto,
  type ICourseScheduleMetricItem,
} from "@ryde/shared";

import { CourseIndicatorIcon } from "../../grid/cell-templates/rides/summary/course.indicator.icon";
import { decorateWithMark } from "../../grid/generic/table/table-defs/helpers/decorateWithMark";
import { FramerTooltip } from "../tooltips";
import { CourseSchedulesPreviewWrapper } from "./course-schedules-preview.styled";
import {
  buildCourseScheduleRouteTypeTooltip,
  resolveScheduleRouteType,
} from "./course-schedules-preview.tooltip";
import { useCourseSchedulesPreviewCourses } from "./use-course-schedules-preview-courses";

interface ICourseSchedulesPreviewContentProps {
  schedules?: ICourseScheduleMetricItem[];
}

type TDirectionFilter = DirectionEnum.PickUp | DirectionEnum.DropOff | "";

const decorateWithSearch = (value: string, searchValue: string): ReactNode => {
  const search = searchValue.trim();
  if (!search) {
    return value;
  }

  if (!value.toLowerCase().match(escapeRegExp(search).toLowerCase())) {
    return value;
  }

  return decorateWithMark(value, search);
};

const buildCourseLabel = (
  courseId: number,
  coursesById: Record<number, ICourseDto>,
): string => {
  const course = coursesById[courseId];
  return course?.name ? `#${courseId} ${course.name}` : `#${courseId}`;
};

const CourseSchedulesExistingRideCell: FC<{
  existingRideId?: number;
  existingRideStatusText?: string;
  canOpenRideDrawer: boolean;
  onRideClick: (rideId: number) => void;
}> = ({
  existingRideId,
  existingRideStatusText,
  canOpenRideDrawer,
  onRideClick,
}) => {
  if (!existingRideId) {
    return <>{NO_VALUE_DISPLAY}</>;
  }

  const statusSuffix = existingRideStatusText
    ? `(${existingRideStatusText})`
    : "";

  if (!canOpenRideDrawer) {
    return (
      <span className="course-schedules__ride-id">
        #{existingRideId}
        {statusSuffix ? (
          <span className="course-schedules__ride-status">{statusSuffix}</span>
        ) : null}
      </span>
    );
  }

  return (
    <span className="course-schedules__ride-id-row">
      <span
        className="course-schedules__ride-id course-schedules__ride-id--clickable"
        role="button"
        tabIndex={0}
        onClick={() => onRideClick(existingRideId)}
        onKeyDown={onActivateKeyDown(() => onRideClick(existingRideId))}
      >
        #{existingRideId}
      </span>
      {statusSuffix ? (
        <span className="course-schedules__ride-status">{statusSuffix}</span>
      ) : null}
    </span>
  );
};

export const CourseSchedulesPreviewContent: FC<
  ICourseSchedulesPreviewContentProps
> = ({ schedules }) => {
  const { t } = useTranslation();
  const { timezone } = useTimezone();
  const { timeFormat } = useFormats();
  const { openCourseEditFormDrawer, openRideEditFormDrawer } = useDrawers();
  const { canOpenCourseDrawer, hasPermissionEditRide, hasPermissionViewRide } =
    useHaveCustomPermissions();
  const canOpenRideDrawer = hasPermissionEditRide || hasPermissionViewRide;
  const [directionFilter, setDirectionFilter] = useState<TDirectionFilter>("");
  const [courseSearch, setCourseSearch] = useState("");
  const directionFilterLabel = t(
    "rideProduction.courseSchedulesPreviewDirection",
  );

  const sortedSchedules = useMemo(() => {
    if (!schedules?.length) {
      return [];
    }
    // Earliest scheduled first
    return [...schedules].sort((left, right) =>
      left.scheduledAt.localeCompare(right.scheduledAt),
    );
  }, [schedules]);

  const courseIds = useMemo(
    () => [...new Set(sortedSchedules.map(schedule => schedule.courseId))],
    [sortedSchedules],
  );
  const { coursesById, isLoadingCourses } =
    useCourseSchedulesPreviewCourses(courseIds);

  const filteredSchedules = useMemo(() => {
    const search = courseSearch.trim().toLowerCase();

    return sortedSchedules.filter(schedule => {
      if (directionFilter && schedule.direction !== directionFilter) {
        return false;
      }

      if (!search) {
        return true;
      }

      const courseLabel = buildCourseLabel(
        schedule.courseId,
        coursesById,
      ).toLowerCase();
      return courseLabel.includes(search);
    });
  }, [sortedSchedules, directionFilter, courseSearch, coursesById]);

  const resolveDirectionLabel = (direction: DirectionEnum): string => {
    switch (direction) {
      case DirectionEnum.PickUp:
        return t("selectListLabel.DirectionA");
      case DirectionEnum.DropOff:
        return t("selectListLabel.DirectionB");
      default:
        return direction;
    }
  };

  const resolveDirectionClassName = (direction: DirectionEnum): string => {
    switch (direction) {
      case DirectionEnum.PickUp:
        return "course-schedules__direction course-schedules__direction--a";
      case DirectionEnum.DropOff:
        return "course-schedules__direction course-schedules__direction--b";
      default:
        return "course-schedules__direction";
    }
  };

  const showExistingRideColumns = useMemo(
    () =>
      sortedSchedules.some(
        schedule =>
          Boolean(schedule.existingRideId) ||
          Boolean(schedule.existingRideStatus),
      ),
    [sortedSchedules],
  );

  const resolveExistingStatusLabel = (
    schedule: ICourseScheduleMetricItem,
  ): string =>
    resolveCourseScheduleExistingStatusLabel({
      t,
      existingRideId: schedule.existingRideId,
      existingRideStatus: schedule.existingRideStatus,
    });

  const handleCourseClick = useEvent((courseId: number) => {
    if (canOpenCourseDrawer && courseId) {
      openCourseEditFormDrawer({ id: courseId });
    }
  });

  const handleRideClick = useEvent((rideId: number) => {
    if (canOpenRideDrawer && rideId) {
      void openRideEditFormDrawer({ id: rideId });
    }
  });

  const handleDirectionChange = useEvent(
    (event: ChangeEvent<HTMLInputElement>) => {
      setDirectionFilter(event.target.value as TDirectionFilter);
    },
  );

  const handleCourseSearchChange = useEvent(
    (event: ChangeEvent<HTMLInputElement>) => {
      setCourseSearch(event.target.value);
    },
  );

  if (!sortedSchedules.length) {
    return (
      <CourseSchedulesPreviewWrapper>
        <div className="course-schedules__table">
          <div className="course-schedules__empty">
            {t("rideProduction.courseSchedulesPreviewEmpty")}
          </div>
        </div>
      </CourseSchedulesPreviewWrapper>
    );
  }

  return (
    <CourseSchedulesPreviewWrapper>
      <div className="course-schedules__filters">
        <TextField
          select
          size="small"
          className="course-schedules__filter course-schedules__filter--direction"
          label={directionFilterLabel}
          value={directionFilter}
          onChange={handleDirectionChange}
          slotProps={{
            select: {
              displayEmpty: true,
              renderValue: selected => {
                if (!selected) {
                  return "";
                }
                return resolveDirectionLabel(selected as DirectionEnum);
              },
            },
          }}
        >
          <MenuItem value="">
            <span className="course-schedules__filter-empty">
              {t("rideProduction.courseSchedulesPreviewDirectionEmpty")}
            </span>
          </MenuItem>
          <MenuItem value={DirectionEnum.PickUp}>
            {t("selectListLabel.DirectionA")}
          </MenuItem>
          <MenuItem value={DirectionEnum.DropOff}>
            {t("selectListLabel.DirectionB")}
          </MenuItem>
        </TextField>

        <TextField
          size="small"
          className="course-schedules__filter course-schedules__filter--course"
          label={t("rideProduction.courseSchedulesPreviewCourse")}
          value={courseSearch}
          onChange={handleCourseSearchChange}
        />
      </div>

      <div className="course-schedules__count">
        {t("rideProduction.courseSchedulesPreviewCount", {
          count: filteredSchedules.length,
        })}
      </div>

      <div
        className={
          showExistingRideColumns
            ? "course-schedules__table course-schedules__table--with-existing"
            : "course-schedules__table"
        }
      >
        <div className="course-schedules__row course-schedules__row--header">
          <span>{t("rideProduction.courseSchedulesPreviewCourse")}</span>
          <span className="course-schedules__direction">
            {t("rideProduction.courseSchedulesPreviewDirection")}
          </span>
          <span className="course-schedules__when">
            {t("rideProduction.courseSchedulesPreviewWhen")}
          </span>
          {showExistingRideColumns ? (
            <>
              <span className="course-schedules__ride">
                {t("rideProduction.courseSchedulesPreviewRide")}
              </span>
              <span className="course-schedules__status">
                {t("rideProduction.courseSchedulesPreviewStatus")}
              </span>
            </>
          ) : null}
        </div>
        {isLoadingCourses && (
          <div className="course-schedules__loading">
            {t("general.loading")}
          </div>
        )}
        {!filteredSchedules.length && !isLoadingCourses && (
          <div className="course-schedules__empty">
            {t("general.NO_RESULTS")}
          </div>
        )}
        {filteredSchedules.map(schedule => {
          const course = coursesById[schedule.courseId];
          const whenLabel = schedule.scheduledAt
            ? formatDate({
                dateISO: schedule.scheduledAt,
                dateFormat: `${MAIN_DATE_FORMAT} ${timeFormat}`,
                timezone,
              })
            : NO_VALUE_DISPLAY;
          const rowKey = `${schedule.courseId}-${schedule.direction}-${schedule.scheduledAt}`;
          const routeType = resolveScheduleRouteType(schedule, course);
          const routeTypeTooltip = buildCourseScheduleRouteTypeTooltip({
            t,
            schedule,
            course,
          });

          return (
            <div className="course-schedules__row" key={rowKey}>
              <div className="course-schedules__course-cell">
                <FramerTooltip content={routeTypeTooltip}>
                  <span className="course-schedules__type-icon">
                    <CourseIndicatorIcon routeType={routeType} />
                  </span>
                </FramerTooltip>
                <span
                  className={
                    canOpenCourseDrawer
                      ? "course-schedules__course-id course-schedules__course-id--clickable"
                      : "course-schedules__course-id"
                  }
                  role={canOpenCourseDrawer ? "button" : undefined}
                  tabIndex={canOpenCourseDrawer ? 0 : undefined}
                  onClick={() => handleCourseClick(schedule.courseId)}
                  onKeyDown={
                    canOpenCourseDrawer
                      ? onActivateKeyDown(() =>
                          handleCourseClick(schedule.courseId),
                        )
                      : undefined
                  }
                >
                  {decorateWithSearch(`#${schedule.courseId}`, courseSearch)}
                </span>
                {course?.name ? (
                  <span className="course-schedules__course-name">
                    {decorateWithSearch(course.name, courseSearch)}
                  </span>
                ) : null}
              </div>
              <span className={resolveDirectionClassName(schedule.direction)}>
                {resolveDirectionLabel(schedule.direction)}
              </span>
              <span className="course-schedules__when">{whenLabel}</span>
              {showExistingRideColumns ? (
                <>
                  <span className="course-schedules__ride">
                    <CourseSchedulesExistingRideCell
                      existingRideId={schedule.existingRideId}
                      existingRideStatusText={resolveCourseScheduleExistingRideStatusText(
                        {
                          t,
                          existingRideStatus: schedule.existingRideStatus,
                        },
                      )}
                      canOpenRideDrawer={canOpenRideDrawer}
                      onRideClick={handleRideClick}
                    />
                  </span>
                  <span
                    className={
                      schedule.existingRideStatus === RideStatusEnum.Canceled
                        ? "course-schedules__status course-schedules__status--canceled"
                        : "course-schedules__status"
                    }
                  >
                    {resolveExistingStatusLabel(schedule)}
                  </span>
                </>
              ) : null}
            </div>
          );
        })}
      </div>
    </CourseSchedulesPreviewWrapper>
  );
};
